import { RecentSelections } from '@seen/core/models/recentSelections'
import { cn } from '@seen/ui'
import { IconSparkles } from '@tabler/icons-react'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  COMBOBOX_CREATE_VALUE,
  COMBOBOX_LIST_MAX_HEIGHT,
  COMBOBOX_NULL_OPTION,
  type ComboboxOption,
  fromComboboxValue,
  MAX_OPTIONS_TO_SHOW,
  NUM_OPTIONS_TO_SHOW_WITHOUT_PRE_SORTING,
  type SuggestedOption,
  toComboboxValue,
  useComboboxFiltering,
} from '#src/components/core/combobox/util.ts'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '#src/components/core/Command.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export type { ComboboxOption, SuggestedOption }

export interface ComboboxProps<TValue extends string | null> {
  options: ComboboxOption<TValue>[]
  onSelect: (value: TValue) => void
  children: ReactNode
  searchPlaceholder?: string
  className?: string
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => any
  disabled?: boolean
  suggestedOptions?: SuggestedOption<TValue>[]
  // Heading above the suggested options. Defaults to "Suggested"; `null` shows
  // no heading.
  suggestedLabel?: string | null
  // Forwarded to ComboboxCommand. Set when the option list contains items that
  // fuzzy-match arbitrary create text (e.g. existing labels), so the create row
  // isn't suppressed the moment a similar option matches.
  alwaysShowCreate?: boolean
  // Pins the create row to the bottom edge of the list's scroll viewport so it
  // stays visible without scrolling past the matches. Default false keeps the
  // row inline at the end of the list.
  stickyCreate?: boolean
  /** Fuse.js match threshold forwarded to ComboboxCommand. 0 = exact, 1 = match anything; defaults to Fuse's 0.6. */
  fuseThreshold?: number
  // Overrides the option list's max height (default 50vh-based). Set a smaller
  // cap when the trigger sits low in a dialog so the popover fits below it
  // instead of collision-flipping above.
  listClassName?: string
  // Sort the browse list by the current user's recent selections before boost.
  // Defaults to true. Set false where an explicit ranking (e.g. specialty match)
  // should take precedence over what the user happened to pick last.
  prioritizeRecents?: boolean
  /**
   * Optional controlled open state. Pass both to own the popover's
   * open/close — e.g. so a custom row `label` can close it directly via
   * `onOpenChange(false)`. Omit both to let the Combobox manage it.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Combobox<TValue extends string | null>({
  onSelect,
  options,
  children,
  searchPlaceholder = 'Search...',
  className,
  createLabel,
  onCreate,
  disabled = false,
  suggestedOptions,
  suggestedLabel = 'Suggested',
  alwaysShowCreate = false,
  stickyCreate = false,
  fuseThreshold,
  listClassName,
  prioritizeRecents = true,
  open: controlledOpen,
  onOpenChange,
}: ComboboxProps<TValue>): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = useCallback(
    (next: boolean): void => {
      if (!isControlled) {
        setInternalOpen(next)
      }
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const handleSelect = useCallback(
    (value: string | null): void => {
      setOpen(false)
      onSelect(value as TValue)
    },
    [onSelect, setOpen]
  )

  const handleCreate = useCallback(
    (search: string): void => {
      onCreate?.(search)
      setOpen(false)
    },
    [onCreate, setOpen]
  )

  return (
    <Popover open={!disabled && open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent className={cn(className, 'p-0')} align="start">
        <ComboboxCommand
          onSelect={handleSelect}
          options={options}
          searchPlaceholder={searchPlaceholder}
          createLabel={createLabel}
          onCreate={handleCreate}
          suggestedOptions={suggestedOptions}
          suggestedLabel={suggestedLabel ?? undefined}
          alwaysShowCreate={alwaysShowCreate}
          stickyCreate={stickyCreate}
          fuseThreshold={fuseThreshold}
          listClassName={listClassName}
          prioritizeRecents={prioritizeRecents}
        />
      </PopoverContent>
    </Popover>
  )
}

interface ComboboxCommandProps<TValue extends string | null> {
  onSelect: (value: TValue) => void
  options: ComboboxOption<TValue>[]
  searchPlaceholder?: string
  autoFocus?: boolean
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => any
  suggestedOptions?: SuggestedOption<TValue>[]
  // Heading above the suggested options. Omit for no heading.
  suggestedLabel?: string
  // Sort the browse list by the user's recent selections before boost. Defaults
  // to true; set false to let an explicit ranking win over recency.
  prioritizeRecents?: boolean
  /** Fuse.js match threshold. 0 = exact match, 1 = match anything. Defaults to Fuse's default (0.6). */
  fuseThreshold?: number
  /** Optional grouping key. After Fuse ranking, items sharing a group key are placed adjacently at the rank of their highest-scoring member. */
  groupBy?: (option: ComboboxOption<TValue>) => string | undefined
  /** Called with the debounced search input. Use to drive async option loading; the parent still feeds results back via `options`. */
  onSearchChange?: (search: string) => void
  // Show the create row even when other options match the search. Default false
  // suppresses it whenever any option matches (avoids a create row beside valid
  // hits). Callers whose option list always contains unrelated matches (e.g. the
  // mention picker's note templates shadowing a condition-create row) set true.
  alwaysShowCreate?: boolean
  // Pins the create row to the bottom edge of the scroll viewport (sticky) so
  // it stays visible however long the match list is. Default false.
  stickyCreate?: boolean
  // Overrides the list's default 50vh-based max height.
  listClassName?: string
}

export function ComboboxCommand<TValue extends string | null>({
  onSelect,
  options,
  searchPlaceholder = 'Search...',
  autoFocus,
  createLabel,
  onCreate,
  suggestedOptions = [],
  suggestedLabel,
  prioritizeRecents = true,
  fuseThreshold,
  groupBy,
  onSearchChange,
  alwaysShowCreate = false,
  stickyCreate = false,
  listClassName,
}: ComboboxCommandProps<TValue>): JSX.Element {
  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner?.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate

  const maybePreSortedOptions = useMaybeSortByRecentSelections(
    options,
    prioritizeRecents
  )

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
  }, 50)

  // Mirror the internal search state out to the parent so async option loaders
  // re-sync (to `''`) when this component unmounts/remounts — e.g. when the
  // mention picker switches between observation-autocomplete, inline-condition,
  // and the main combobox surface.
  useEffect(() => {
    onSearchChange?.(search)
  }, [search, onSearchChange])

  const { filteredSuggestedOptions, filteredOptions } = useComboboxFiltering({
    options: maybePreSortedOptions,
    suggestedOptions,
    search,
    fuseThreshold,
    groupBy,
  })

  const firstValue =
    filteredSuggestedOptions[0]?.value ?? filteredOptions[0]?.value ?? ''
  const [highlightedValue, setHighlightedValue] = useState(firstValue)
  const didFirstValueChange = useDidChange(firstValue)
  if (didFirstValueChange) {
    setHighlightedValue(firstValue)
  }

  const handleItemSelect = useCallback(
    (selectedValue: string) => {
      onSelect(fromComboboxValue<TValue>(selectedValue))
      if (
        recentSelections &&
        selectedValue &&
        selectedValue !== COMBOBOX_NULL_OPTION
      ) {
        patchRecentSelections({
          instance: recentSelections,
          mutateFn: (rs) => rs.add(selectedValue),
        })
      }
    },
    [onSelect, recentSelections, patchRecentSelections]
  )

  const showCreateRow =
    search.length > 1 &&
    !!createLabel &&
    (alwaysShowCreate || filteredOptions.length === 0)
  const createRow = createLabel && (
    <CommandItem
      value={COMBOBOX_CREATE_VALUE}
      onSelect={() => onCreate?.(search)}
      // Opaque squared bar pinned to the list's bottom edge. The list drops its
      // bottom padding when sticky (below) so no gutter is left for a scrolling
      // row to peek through beneath this bar.
      className={cn(
        stickyCreate && 'sticky bottom-0 z-10 rounded-none border-t bg-popover'
      )}
    >
      {createLabel(search)}
    </CommandItem>
  )

  return (
    <Command
      shouldFilter={false}
      // value needs to be controlled for highlighting to work properly if shouldFilter is false
      // (i.e. we highlight the first option above when the search text changes)
      value={highlightedValue}
      onValueChange={setHighlightedValue}
    >
      {/* We don't use the value prop because when it's used, the list scroll position
          doesn't reset properly when you press backspace in the input. */}
      <CommandInput
        onValueChange={debouncedSearch}
        placeholder={searchPlaceholder}
        className="rounded-none border-b px-3"
        autoFocus={autoFocus}
      />
      {/* We use a key to reset the scroll position when the search text changes. */}
      <CommandList
        key={search}
        // Drop the bottom padding when a sticky create row is pinned there:
        // overflow clips at the padding box, so a 4px bottom gutter would let a
        // scrolling row show beneath the bar.
        className={cn(
          COMBOBOX_LIST_MAX_HEIGHT,
          stickyCreate && 'pb-0',
          listClassName
        )}
      >
        <CommandEmpty>None found.</CommandEmpty>
        {!search && filteredSuggestedOptions.length > 0 && (
          <>
            <CommandGroup
              heading={
                suggestedLabel ? (
                  <span className="flex items-center gap-1.5">
                    <IconSparkles className="size-3.5 shrink-0 text-green-600" />
                    {suggestedLabel}
                  </span>
                ) : undefined
              }
            >
              {filteredSuggestedOptions.map((option) => (
                <CommandItem
                  key={`suggested-${option.value}`}
                  value={toComboboxValue(option.value)}
                  onSelect={handleItemSelect}
                  className="group items-center justify-between gap-2"
                >
                  <div className="flex-1">{option.label}</div>
                  {option.tooltip}
                </CommandItem>
              ))}
            </CommandGroup>
            {filteredOptions.length > 0 && <CommandSeparator />}
          </>
        )}
        {search && filteredSuggestedOptions.length > 0 && (
          <>
            <CommandGroup>
              {filteredSuggestedOptions.map((option) => (
                <CommandItem
                  key={`suggested-${option.value}`}
                  value={toComboboxValue(option.value)}
                  onSelect={handleItemSelect}
                  className="group items-center justify-between gap-2"
                >
                  <div className="flex-1">{option.label}</div>
                  {option.tooltip}
                </CommandItem>
              ))}
            </CommandGroup>
            {filteredOptions.length > 0 && <CommandSeparator />}
          </>
        )}
        <CommandGroup>
          {filteredOptions.slice(0, MAX_OPTIONS_TO_SHOW).map((option) => (
            <CommandItem
              key={option.value}
              value={toComboboxValue(option.value)}
              onSelect={handleItemSelect}
              className="group items-center justify-between gap-2"
            >
              {option.label}
            </CommandItem>
          ))}
          {!stickyCreate && showCreateRow && createRow}
          {filteredOptions.length > MAX_OPTIONS_TO_SHOW && (
            <CommandItem disabled>Search to see more results…</CommandItem>
          )}
        </CommandGroup>
        {/* Outside the CommandGroup: its overflow-hidden box would trap the
            sticky positioning; as a direct list child the row pins to the
            scroll viewport's bottom edge. */}
        {stickyCreate && showCreateRow && createRow}
      </CommandList>
    </Command>
  )
}

function useMaybeSortByRecentSelections<TValue extends string | null>(
  options: ComboboxOption<TValue>[],
  prioritizeRecents: boolean
): ComboboxOption<TValue>[] {
  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )

  return useMemo(() => {
    const recentValues = recentSelections?.values ?? []
    // Recency primary, boost (preferred/available) secondary, original order last.
    // Recency reshuffle only kicks in for longer, scrollable lists, and only when
    // the caller opts into it (an explicit ranking may need to win instead).
    const useRecency =
      prioritizeRecents &&
      options.length > NUM_OPTIONS_TO_SHOW_WITHOUT_PRE_SORTING
    return [...options].sort(
      (a, b) =>
        (useRecency ? compareRecency(a, b, recentValues) : 0) ||
        compareBoost(a, b)
    )
  }, [options, recentSelections, prioritizeRecents])
}

function compareBoost<TValue extends string | null>(
  a: ComboboxOption<TValue>,
  b: ComboboxOption<TValue>
): number {
  if (isPinnedOption(a) || isPinnedOption(b)) {
    return 0
  }
  return (b.boost ?? 0) - (a.boost ?? 0)
}

function compareRecency<TValue extends string | null>(
  a: ComboboxOption<TValue>,
  b: ComboboxOption<TValue>,
  recentValues: string[]
): number {
  if (isPinnedOption(a) || isPinnedOption(b)) {
    return 0
  }
  const aIndex = recentValues.indexOf(a.value as string)
  const bIndex = recentValues.indexOf(b.value as string)
  if (aIndex === -1 && bIndex === -1) {
    return 0
  }
  if (aIndex === -1) {
    return 1
  }
  if (bIndex === -1) {
    return -1
  }
  return aIndex - bIndex
}

function isPinnedOption<TValue extends string | null>(
  option: ComboboxOption<TValue>
): boolean {
  return option.value === null || option.value === COMBOBOX_CREATE_VALUE
}
